/**
 * Social-privacy "levels" — web's mirror of the mobile app's Full / Limited /
 * Minimum visibility model.
 *
 * "Level" is purely client-derived: the backend stores only per-key booleans on
 * `SocialPreferencesVo` (POST `/users/{id}/social/sharing_preference`), with no
 * server notion of a level. Mobile's level system touches exactly NINE managed
 * keys and writes them as a locked group; the other six keys
 * (`top_holders`, `metric_roi`, `metric_dollars_traded`,
 * `metric_dollars_investment`, `metric_portfolio_value`,
 * `metric_signed_open_position`) are outside the managed level set and are
 * deliberately excluded from level derivation, matching mobile.
 *
 * EXCEPTION: `metric_dollars_traded` gates the profile-header "Volume" stat but
 * was dropped from mobile's level sets, leaving it stuck false (Volume hidden).
 * Web writes it alongside every level change until mobile re-adds it to its
 * level sets in a follow-up build. Other orphan keys, including the legacy
 * `top_holders` preference, are neither derived nor written. This preserves old
 * settings and avoids sending options unsupported by the web API.
 */
import type { SocialPreferencesVo } from 'models';

export type SharingLevel = 'full' | 'limited' | 'minimum';

/** The nine keys mobile's level system manages. Orphan keys are excluded. */
export type ManagedSharingKey =
    | 'metric_num_markets_traded'
    | 'metric_volume'
    | 'metric_pnl'
    | 'metric_category_positive_pnl'
    | 'metric_category_pnl'
    | 'activity'
    | 'portfolio'
    | 'closed_portfolio'
    | 'metric_open_interest';

/**
 * Each level's enabled key set. Full and Limited mirror mobile. Web intentionally
 * makes Minimum an empty set so it is a real leaderboard opt-out; mobile's
 * current map leaves predictions enabled, which conflicts with this surface's
 * explicit Join/Leave contract.
 */
const LEVEL_ENABLED_KEYS: Record<SharingLevel, readonly ManagedSharingKey[]> = {
    full: [
        'metric_num_markets_traded',
        'metric_volume',
        'metric_pnl',
        'metric_category_positive_pnl',
        'metric_category_pnl',
        'activity',
        'portfolio',
        'closed_portfolio',
        'metric_open_interest',
    ],
    limited: [
        'metric_num_markets_traded',
        'metric_volume',
        'metric_pnl',
        'metric_category_positive_pnl',
        'metric_category_pnl',
    ],
    minimum: [],
};

/** Preferences that can place a user on a social statistics leaderboard. */
export const SOCIAL_LEADERBOARD_SHARING_KEYS: readonly ManagedSharingKey[] = [
    'metric_num_markets_traded',
    'metric_volume',
    'metric_pnl',
    'metric_category_positive_pnl',
    'metric_category_pnl',
];

/** Includes partial old-style configurations without migrating them. */
export function sharesSocialLeaderboardMetrics(prefs: SocialPreferencesVo): boolean {
    return SOCIAL_LEADERBOARD_SHARING_KEYS.some((key) => prefs[key]);
}

/** All nine managed keys (== the Full enabled set). */
export const MANAGED_SHARING_KEYS: readonly ManagedSharingKey[] = LEVEL_ENABLED_KEYS.full;

/**
 * Write-only override for `metric_dollars_traded`: it gates the profile-header
 * Volume stat but was dropped from mobile's level sets, leaving it stuck false.
 * Web re-adds it as a Full-only write (true at Full, false at Limited/Minimum)
 * until mobile restores it to its level sets next build. It rides alongside the
 * managed-key writes but is excluded from level *derivation*, so the 9-key
 * managed model is unchanged.
 */
const VOLUME_WRITE_KEY = 'metric_dollars_traded' satisfies keyof SocialPreferencesVo;

export type SharingLevelWriteKey = ManagedSharingKey | typeof VOLUME_WRITE_KEY;

/** Derivation checks levels highest-first and takes the first fully-satisfied set. */
const LEVELS_HIGH_TO_LOW: readonly SharingLevel[] = ['full', 'limited', 'minimum'];

/**
 * Full-overwrite target for a level: every managed key mapped to its boolean for
 * that level (enabled → `true`, otherwise `false`). Like mobile, web POSTs all
 * nine managed keys on every level change rather than sending a delta.
 *
 * Plus the write-only target `metric_dollars_traded`, which is Full-only. Legacy
 * orphan preferences such as `top_holders` are intentionally left untouched.
 */
export function getSharingLevelTargets(level: SharingLevel): Record<SharingLevelWriteKey, boolean> {
    const enabled = new Set<ManagedSharingKey>(LEVEL_ENABLED_KEYS[level]);
    const targets = MANAGED_SHARING_KEYS.reduce(
        (acc, key) => {
            acc[key] = enabled.has(key);
            return acc;
        },
        {} as Record<SharingLevelWriteKey, boolean>,
    );
    targets[VOLUME_WRITE_KEY] = level === 'full';
    return targets;
}

/**
 * Derive the level from a stored preference map: the highest level whose enabled
 * keys are ALL true. Like mobile (`social_profile.dart`), extra keys being on do
 * not disqualify a level, and unmatched/old-style maps floor to `minimum`.
 *
 * NOTE: mobile defaults a profile with *no stored prefs* to `limited`. Web's
 * default `SocialPreferencesVo` is all-false (→ `minimum` here), so the
 * "no prefs → limited" default is a load/migration concern for the caller, not
 * this pure function.
 */
export function deriveSharingLevel(prefs: SocialPreferencesVo): SharingLevel {
    const isSatisfied = (level: SharingLevel): boolean => LEVEL_ENABLED_KEYS[level].every((key) => prefs[key]);
    return LEVELS_HIGH_TO_LOW.find(isSatisfied) ?? 'minimum';
}

/** Render order for the selector: most-shared first. */
export const SHARING_LEVEL_ORDER: readonly SharingLevel[] = ['full', 'limited', 'minimum'];

export interface SharingLevelCopy {
    label: string;
    description: string;
}

/** Base copy shared by profile visibility and leaderboard enrollment. */
export const SHARING_LEVEL_COPY: Record<SharingLevel, SharingLevelCopy> = {
    full: { label: 'Full', description: 'Your entire profile, including trading activity' },
    limited: {
        label: 'Limited',
        description: 'Your entire profile, but your trading activity stays hidden',
    },
    minimum: { label: 'Minimum', description: 'Posts and followers only' },
};

export function getSharingLevelDescription(level: SharingLevel, hasMarginAccount: boolean): string {
    const base = SHARING_LEVEL_COPY[level].description;

    if (level === 'full') {
        return hasMarginAccount
            ? `${base}. Eligible for all leaderboards.`
            : `${base}. Eligible for social leaderboards.`;
    }

    if (level === 'limited') {
        return `${base}. Eligible for social leaderboards only.`;
    }

    return `${base}. Hidden from all leaderboards.`;
}
